import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
`;

const Card = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-top: 5px solid var(--primary-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Title = styled.h1`
  color: var(--primary-color);
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  margin-bottom: 32px;
  font-size: 16px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  text-align: right;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 16px;
  direction: rtl;
  text-align: right;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 16px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--primary-hover);
  }

  &:disabled {
    background-color: var(--text-tertiary);
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'error' | 'success' }>`
  background-color: ${props => props.type === 'error' ? 'var(--error-light)' : 'var(--success-light)'};
  color: ${props => props.type === 'error' ? 'var(--error-color)' : 'var(--success-color)'};
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  text-align: right;
  border: 1px solid ${props => props.type === 'error' ? 'var(--error-color)' : 'var(--success-color)'};
`;

const LinkContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [emailError, setEmailError] = useState('');

  const { forgotPassword } = useAuth();

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setEmailError('דוא"ל הוא שדה חובה');
      return false;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('דוא"ל לא תקין');
      return false;
    }
    
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await forgotPassword({ email });

      if (result.isSuccess) {
        setMessage({
          type: 'success',
          text: result.message || 'אם הדוא"ל קיים במערכת, נשלח אליך קישור לאיפוס סיסמה'
        });
        setEmail('');
      } else {
        setMessage({
          type: 'error',
          text: result.message || 'שגיאה בשליחת בקשת איפוס סיסמה'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'שגיאה בשליחת בקשת איפוס סיסמה'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError('');
    }
    if (message) {
      setMessage(null);
    }
  };

  return (
    <Container>
      <Card>
        <Title>איפוס סיסמה</Title>
        <Subtitle>
          הזן את כתובת הדוא"ל שלך ונשלח לך קישור לאיפוס סיסמה
        </Subtitle>

        {message && (
          <Message type={message.type}>{message.text}</Message>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">דוא"ל</Label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              className={emailError ? 'error' : ''}
              disabled={isLoading}
              autoComplete="username"
            />
            {emailError && (
              <Message type="error">{emailError}</Message>
            )}
          </FormGroup>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'שולח...' : 'שלח קישור לאיפוס'}
          </Button>
        </Form>

        <LinkContainer>
          <StyledLink to="/login">
            חזור להתחברות
          </StyledLink>
        </LinkContainer>
      </Card>
    </Container>
  );
};

export default ForgotPasswordPage;